<template>
  <div>
    <!--Form-->
    <template v-if="!loading">
      <PageLayout contentClass="bg-transparent border-0">
        <template #pageHeader>
          <BackLink :to-link="url" />
        </template>
        <template #pageContent>
          <div>
            <h1 class="font-montserrat text-2xl font-bold text-university-primary">
              Participant Agreement
            </h1>
            <mobile-app-form-completion v-if="showPromotion" formLabel="Participant Agreement" />
            <div v-else id="agreement-no-insurance-or-direct-pay-form" class="mt-10 relative" role="form" />
          </div>
        </template>
      </PageLayout>
    </template>

    <!-- Spinner -->
    <template v-else>
      <CountDown ref="counter" :message="message" @redirecting="(msg) => (message = msg)" />
    </template>
  </div>
</template>

<script>
import tailwind from "@apiabroad/formio-tailwind-template";
import { Formio } from "formiojs";
import { mapState } from "vuex";
import MobileAppFormCompletion from "@/components/forms/MobileAppFormCompletion.vue";
import CountDown from "@/components/forms/SharedComponents/CountDown.vue";
import PageLayout from "@/components/forms/SharedComponents/Layout/PageDefault.vue";
import BackLink from "@/components/forms/SharedComponents/Link.vue";
import formioApiMix from "@/mixins/formIoApi";
import forms from "@/mixins/forms";
import impersonationFormSniffer from "@/mixins/ImpersonationFormSniffer";
import formService from "@/services/form";
import ordersService from "@/services/orders";

export default {
  name: "CustomizedCodeOfConduct",
  components: {
    PageLayout,
    BackLink,
    CountDown,
    MobileAppFormCompletion,
  },
  mixins: [forms, formioApiMix, impersonationFormSniffer],
  data() {
    return {
      loading: true,
      message: "",
      formPath: "participantagreementnoinsuranceordirectpay",
      experienceDetails: null,
    };
  },
  computed: {
    ...mapState(["featureFlags", "currentUser"]),
    showPromotion() {
      return Boolean(
        this.experienceDetails &&
          this.experienceDetails.show_mobile_app_promotions &&
          this.featureFlags["force-mobile-form-adoption"],
      );
    },
    applicationId() {
      return this.$route.params.applicationId;
    },
    url() {
      return {
        name: "applications/landing",
        params: { applicationId: this.applicationId },
      };
    },
    formUrl() {
      let url = `/api/forms/${this.formPath}`;
      if (this.submission_id) url += "/submission/" + this.submission_id;
      return url;
    },
  },
  watch: {
    loading: {
      handler: function (newval) {
        if (!newval) {
          this.message = "Rendering form";
          setTimeout(() => {
            this.loadForm();
          }, 1000);
        }
      },
    },
  },
  async created() {
    // Check if form rule is on "form_rules" or "post_acceptance_form_rules"
    const canView = await this.canView();
    if (!canView) {
      this.$refs.counter.startCountDown();
      return;
    }
    // Try to find form submission
    const data = await this.checkSubmission();
    if (data) {
      this.submission_id = data;
    }

    try {
      const experienceSummary = await ordersService.getMyExperiencesSummary(this.currentUser?.participantId);
      this.experienceDetails = experienceSummary.find((s) => s.application_id === this.applicationId);
    } catch (err) {
      datadogLogs.logger.error(
        "Error loading experience details",
        {
          applicationId: this.applicationId,
        },
        err,
      );
    }

    this.loading = false;
  },
  methods: {
    async canView() {
      this.message = "Validating form, please wait.";
      const inPostRule = await this.isValidApplication(
        this.applicationId,
        "CP Participant Agreement Form - No Insurance or Direct Pay",
        "post_acceptance_form_rules",
      );
      return inPostRule;
    },
    checkSubmission() {
      this.message = "Getting form submission, please wait";
      return this.formioSubmissionExists(this.formPath, this.applicationId);
    },
    loadForm() {
      const element = document.getElementById("agreement-no-insurance-or-direct-pay-form");
      Formio.setBaseUrl(`${window.location.protocol}//${window.location.host}`);
      Formio.setAuthUrl(`${window.location.protocol}//${window.location.host}/api/forms`);
      Formio.use(tailwind);
      Formio.createForm(element, this.formUrl, {
        hooks: {
          beforeSubmit: (submission, next) => {
            submission.data.application_id = this.applicationId;
            submission.data.completedForm = true;
            submission = this.attachEmployeeToSubmission(submission, "post");
            next();
          },
        },
        noAlerts: true,
      }).then((form) => {
        // suppress Form.io SDK default submit behavior so that we can submit
        // to our own backend with all necessary CSRF protections
        form.nosubmit = true;
        form.on("submit", (submission) => {
          formService.createOrUpdateSubmission(this.formPath, submission, this.submission_id).then((submission) => {
            form.emit("submitDone", submission);
          });
        });
        form.on("submitDone", (submission) => {
          this.logSubmission(this.formPath, {}, submission.data);
          this.submission_id = submission._id;
          this.$router.push({
            name: "applications/landing",
            params: {
              applicationId: this.applicationId,
            },
          });
        });
      });
    },
  },
};
</script>
